import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../utils/api';
import { toast } from '../hooks/use-toast';
import { 
  Users, Upload, FileText, GraduationCap, Clock, CheckCircle, 
  AlertCircle, Search, PlusCircle, LogOut, BarChart3, Calendar,
  Briefcase, DollarSign, User, Mail, Phone, BookOpen, Award,
  MessageSquare, ChevronRight, Building2, X
} from 'lucide-react';

const CounselorPortal = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('students');
  
  // Non-partnered cases state
  const [nonPartneredCases, setNonPartneredCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseDetailOpen, setCaseDetailOpen] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [caseSearchQuery, setCaseSearchQuery] = useState('');
  const [caseStatusFilter, setCaseStatusFilter] = useState('all');
  
  const [credentials, setCredentials] = useState({ 
    email: '', 
    password: '', 
    first_name: '', 
    last_name: '', 
    school_name: '' 
  });
  
  const [newStudentEmail, setNewStudentEmail] = useState('');
  const [recommendation, setRecommendation] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('counselor_token');
    if (token) {
      setIsLoggedIn(true);
      loadDashboardData();
    }
  }, []);

  // Counselor uses a separate token, so we create custom config
  const getCounselorConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('counselor_token')}` }
  });

  const loadDashboardData = async () => {
    await Promise.all([loadStudents(), loadStats(), loadDeadlines(), loadNonPartneredCases()]);
  };

  const loadStudents = async () => {
    try {
      const response = await api.get('/counselor/students', getCounselorConfig());
      setStudents(response.data);
    } catch (error) {
      console.error('Error loading students:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/counselor/dashboard/stats', getCounselorConfig());
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadDeadlines = async () => {
    try {
      const response = await api.get('/counselor/dashboard/deadlines', getCounselorConfig());
      setDeadlines(response.data);
    } catch (error) {
      console.error('Error loading deadlines:', error);
    }
  };

  // Non-partnered cases management
  const loadNonPartneredCases = async () => {
    try {
      const response = await api.get('/counselor/non-partnered-cases', getCounselorConfig());
      setNonPartneredCases(response.data);
    } catch (error) {
      console.error('Error loading non-partnered cases:', error);
    }
  };

  const handleViewCaseDetail = async (caseItem) => {
    try {
      const response = await api.get(`/counselor/non-partnered-cases/${caseItem._id}`, getCounselorConfig());
      setSelectedCase(response.data);
      setCaseDetailOpen(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load case details', variant: 'destructive' });
    }
  };

  const handleUpdateCaseStatus = async (caseId, newStatus) => {
    try {
      await api.put(`/counselor/non-partnered-cases/${caseId}/status?status=${newStatus}`, {}, getCounselorConfig());
      toast({ title: 'Success', description: `Status updated to ${newStatus}` });
      loadNonPartneredCases();
      // Update selected case if open
      if (selectedCase && selectedCase._id === caseId) {
        setSelectedCase({ ...selectedCase, status: newStatus });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  const handleAddNote = async (caseId) => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/counselor/non-partnered-cases/${caseId}/notes?note=${encodeURIComponent(newNote)}`, {}, getCounselorConfig());
      toast({ title: 'Success', description: 'Note added' });
      setNewNote('');
      // Refresh case detail
      const response = await api.get(`/counselor/non-partnered-cases/${caseId}`, getCounselorConfig());
      setSelectedCase(response.data);
      loadNonPartneredCases();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to add note', variant: 'destructive' });
    }
  };

  // Filter non-partnered cases
  const filteredCases = nonPartneredCases.filter(c => {
    const matchesSearch = 
      c.student_name?.toLowerCase().includes(caseSearchQuery.toLowerCase()) ||
      c.student_email?.toLowerCase().includes(caseSearchQuery.toLowerCase()) ||
      c.college_name?.toLowerCase().includes(caseSearchQuery.toLowerCase());
    const matchesStatus = caseStatusFilter === 'all' || c.status === caseStatusFilter;
    return matchesSearch && matchesStatus;
  });

  // Get status badge color
  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      assigned: 'bg-blue-100 text-blue-800 border-blue-200',
      in_progress: 'bg-purple-100 text-purple-800 border-purple-200',
      completed: 'bg-green-100 text-green-800 border-green-200'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    try {
      const endpoint = isRegistering ? '/counselor/register' : '/counselor/login';
      const payload = isRegistering 
        ? credentials 
        : { email: credentials.email, password: credentials.password };
      
      const response = await api.post(endpoint, payload);
      localStorage.setItem('counselor_token', response.data.access_token);
      setIsLoggedIn(true);
      loadDashboardData();
      toast({ title: 'Success', description: isRegistering ? 'Account created' : 'Logged in successfully' });
    } catch (error) {
      // Global error handler shows toast
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('counselor_token');
    setIsLoggedIn(false);
    setStudents([]);
    setStats(null);
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    try {
      await api.post('/counselor/students/assign', { student_email: newStudentEmail }, getCounselorConfig());
      toast({ title: 'Success', description: 'Student assigned successfully' });
      setNewStudentEmail('');
      loadDashboardData();
    } catch (error) {
      // Global error handler shows toast
    }
  };

  const handleTranscriptUpload = async (studentId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(
        `/counselor/students/${studentId}/transcript`,
        formData,
        { headers: { ...getCounselorConfig().headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast({ title: 'Success', description: 'Transcript uploaded successfully' });
      loadStudents();
    } catch (error) {
      // Global error handler shows toast
    }
  };

  const handleSubmitRecommendation = async (studentId) => {
    try {
      await api.post(
        `/counselor/students/${studentId}/recommendation`,
        { student_id: studentId, content: recommendation },
        getCounselorConfig()
      );
      toast({ title: 'Success', description: 'Recommendation submitted' });
      setRecommendation('');
      setSelectedStudent(null);
    } catch (error) {
      // Global error handler shows toast
    }
  };

  const filteredStudents = students.filter(s => 
    `${s.first_name} ${s.last_name} ${s.email}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Login/Register Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-2 border-purple-100">
          <CardHeader className="text-center">
            <div className="bg-gradient-to-br from-purple-600 to-indigo-600 p-4 rounded-2xl w-fit mx-auto mb-4 shadow-lg">
              <GraduationCap className="h-10 w-10 text-white" />
            </div>
            <CardTitle className="text-2xl bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
              Counselor Portal
            </CardTitle>
            <CardDescription>
              {isRegistering ? 'Create your counselor account' : 'Sign in to manage your students'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAuth} className="space-y-4">
              {isRegistering && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>First Name</Label>
                      <Input 
                        value={credentials.first_name} 
                        onChange={(e) => setCredentials({...credentials, first_name: e.target.value})} 
                        required 
                      />
                    </div>
                    <div>
                      <Label>Last Name</Label>
                      <Input 
                        value={credentials.last_name} 
                        onChange={(e) => setCredentials({...credentials, last_name: e.target.value})} 
                        required 
                      />
                    </div>
                  </div>
                  <div>
                    <Label>School Name</Label>
                    <Input 
                      value={credentials.school_name} 
                      onChange={(e) => setCredentials({...credentials, school_name: e.target.value})} 
                      required 
                    />
                  </div>
                </>
              )}
              <div>
                <Label>Email</Label>
                <Input 
                  type="email" 
                  value={credentials.email} 
                  onChange={(e) => setCredentials({...credentials, email: e.target.value})} 
                  required 
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input 
                  type="password" 
                  value={credentials.password} 
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})} 
                  required 
                />
              </div>
              <Button type="submit" className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700">
                {isRegistering ? 'Create Account' : 'Sign In'}
              </Button>
              <p className="text-center text-sm text-gray-600">
                {isRegistering ? 'Already have an account? ' : "Don't have an account? "}
                <button 
                  type="button" 
                  onClick={() => setIsRegistering(!isRegistering)} 
                  className="text-purple-600 hover:underline font-medium"
                >
                  {isRegistering ? 'Sign In' : 'Register'}
                </button>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-br from-purple-600 to-indigo-600 p-2 rounded-xl">
              <GraduationCap className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Counselor Portal</h1>
              <p className="text-sm text-gray-600">Wayo College Application Platform</p>
            </div>
          </div>
          <Button variant="outline" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
              <CardContent className="p-4">
                <Users className="h-8 w-8 mb-2 opacity-80" />
                <p className="text-3xl font-bold">{stats.total_students}</p>
                <p className="text-sm opacity-80">Total Students</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white">
              <CardContent className="p-4">
                <FileText className="h-8 w-8 mb-2 opacity-80" />
                <p className="text-3xl font-bold">{stats.total_applications}</p>
                <p className="text-sm opacity-80">Applications</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
              <CardContent className="p-4">
                <CheckCircle className="h-8 w-8 mb-2 opacity-80" />
                <p className="text-3xl font-bold">{stats.submitted_applications}</p>
                <p className="text-sm opacity-80">Submitted</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white">
              <CardContent className="p-4">
                <Upload className="h-8 w-8 mb-2 opacity-80" />
                <p className="text-3xl font-bold">{stats.transcripts_uploaded}</p>
                <p className="text-sm opacity-80">Transcripts</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-pink-500 to-rose-600 text-white">
              <CardContent className="p-4">
                <BarChart3 className="h-8 w-8 mb-2 opacity-80" />
                <p className="text-3xl font-bold">{stats.recommendations_submitted}</p>
                <p className="text-sm opacity-80">Recommendations</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="students" data-testid="tab-students">
              <Users className="h-4 w-4 mr-2" />
              Students
            </TabsTrigger>
            <TabsTrigger value="non-partnered" data-testid="tab-non-partnered">
              <Briefcase className="h-4 w-4 mr-2" />
              Non-Partnered Cases
              {nonPartneredCases.filter(c => c.status === 'pending').length > 0 && (
                <Badge className="ml-2 bg-amber-500 text-white text-xs px-1.5 py-0.5">
                  {nonPartneredCases.filter(c => c.status === 'pending').length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="deadlines" data-testid="tab-deadlines">
              <Calendar className="h-4 w-4 mr-2" />
              Upcoming Deadlines
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              {/* Analytics Overview */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-80">Total Students</p>
                        <p className="text-3xl font-bold">{students.length}</p>
                      </div>
                      <Users className="h-10 w-10 opacity-60" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-80">Applications</p>
                        <p className="text-3xl font-bold">{stats?.total_applications || 0}</p>
                      </div>
                      <FileText className="h-10 w-10 opacity-60" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-80">Pending Cases</p>
                        <p className="text-3xl font-bold">{nonPartneredCases.filter(c => c.status === 'pending').length}</p>
                      </div>
                      <Clock className="h-10 w-10 opacity-60" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-80">Completed</p>
                        <p className="text-3xl font-bold">{nonPartneredCases.filter(c => c.status === 'completed').length}</p>
                      </div>
                      <CheckCircle className="h-10 w-10 opacity-60" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                {/* Application Status Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-emerald-600" />
                      Application Status
                    </CardTitle>
                    <CardDescription>Distribution of application statuses across all students</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {[
                        { label: 'Submitted', count: stats?.submitted_applications || 0, color: 'bg-emerald-500' },
                        { label: 'In Progress', count: stats?.in_progress_applications || 0, color: 'bg-blue-500' },
                        { label: 'Not Started', count: stats?.not_started_applications || 0, color: 'bg-gray-400' },
                        { label: 'Accepted', count: stats?.accepted || 0, color: 'bg-green-500' },
                        { label: 'Waitlisted', count: stats?.waitlisted || 0, color: 'bg-amber-500' },
                        { label: 'Denied', count: stats?.denied || 0, color: 'bg-red-500' },
                      ].map(status => {
                        const total = stats?.total_applications || 1;
                        const percentage = Math.round((status.count / total) * 100) || 0;
                        return (
                          <div key={status.label} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">{status.label}</span>
                              <span className="font-medium">{status.count} ({percentage}%)</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`${status.color} h-2 rounded-full transition-all duration-500`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Case Status Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Briefcase className="h-5 w-5 text-purple-600" />
                      Non-Partnered Cases
                    </CardTitle>
                    <CardDescription>Status of all non-partnered university cases</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {[
                        { label: 'Pending', count: nonPartneredCases.filter(c => c.status === 'pending').length, color: 'bg-amber-500', icon: Clock },
                        { label: 'Assigned', count: nonPartneredCases.filter(c => c.status === 'assigned').length, color: 'bg-blue-500', icon: User },
                        { label: 'In Progress', count: nonPartneredCases.filter(c => c.status === 'in_progress').length, color: 'bg-purple-500', icon: FileText },
                        { label: 'Completed', count: nonPartneredCases.filter(c => c.status === 'completed').length, color: 'bg-emerald-500', icon: CheckCircle },
                      ].map(status => {
                        const total = nonPartneredCases.length || 1;
                        const percentage = Math.round((status.count / total) * 100) || 0;
                        const Icon = status.icon;
                        return (
                          <div key={status.label} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                            <div className={`${status.color} p-2 rounded-lg`}>
                              <Icon className="h-4 w-4 text-white" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{status.label}</span>
                                <span className="text-gray-600">{status.count} cases</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                                <div 
                                  className={`${status.color} h-1.5 rounded-full transition-all duration-500`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Upcoming Deadlines Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-red-600" />
                      Upcoming Deadlines
                    </CardTitle>
                    <CardDescription>Applications due in the next 30 days</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {deadlines.length === 0 ? (
                      <div className="text-center py-6 text-gray-500">
                        <Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" />
                        <p>No upcoming deadlines</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {deadlines.slice(0, 5).map((deadline, idx) => {
                          const daysLeft = Math.ceil((new Date(deadline.deadline) - new Date()) / (1000 * 60 * 60 * 24));
                          return (
                            <div key={idx} className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                              <div className="flex items-center gap-3">
                                <div className={`w-2 h-2 rounded-full ${daysLeft <= 3 ? 'bg-red-500' : daysLeft <= 7 ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                                <div>
                                  <p className="font-medium">{deadline.student_name}</p>
                                  <p className="text-sm text-gray-500">{deadline.college_name}</p>
                                </div>
                              </div>
                              <Badge variant={daysLeft <= 3 ? 'destructive' : daysLeft <= 7 ? 'warning' : 'secondary'}>
                                {daysLeft} days
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Student Activity */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-blue-600" />
                      Student Activity
                    </CardTitle>
                    <CardDescription>Recent student progress overview</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {students.slice(0, 5).map(student => (
                        <div key={student._id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                            {student.first_name?.charAt(0)}{student.last_name?.charAt(0)}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{student.first_name} {student.last_name}</p>
                            <p className="text-sm text-gray-500">{student.applications?.length || 0} applications</p>
                          </div>
                          <div className="text-right">
                            <Progress value={student.avg_progress || 0} className="w-20 h-2" />
                            <p className="text-xs text-gray-500 mt-1">{student.avg_progress || 0}% complete</p>
                          </div>
                        </div>
                      ))}
                      {students.length === 0 && (
                        <div className="text-center py-6 text-gray-500">
                          <Users className="h-10 w-10 mx-auto mb-2 opacity-50" />
                          <p>No students assigned yet</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Student List */}
              <div className="lg:col-span-2 space-y-4">
                <Card>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <CardTitle>My Students</CardTitle>
                      <div className="flex items-center space-x-2">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input 
                            placeholder="Search students..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 w-64"
                          />
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {filteredStudents.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No students assigned yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {filteredStudents.map(student => (
                          <div 
                            key={student._id} 
                            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                              selectedStudent?._id === student._id 
                                ? 'border-purple-500 bg-purple-50' 
                                : 'border-gray-100 hover:border-purple-200 hover:bg-gray-50'
                            }`}
                            onClick={() => setSelectedStudent(student)}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="font-semibold text-lg">{student.first_name} {student.last_name}</h3>
                                <p className="text-sm text-gray-600">{student.email}</p>
                              </div>
                              <div className="flex items-center space-x-3">
                                <div className="text-right">
                                  <p className="text-sm font-medium">{student.applications_count || 0} apps</p>
                                  <p className="text-xs text-gray-500">{student.submitted_count || 0} submitted</p>
                                </div>
                                {student.has_transcript ? (
                                  <Badge className="bg-green-100 text-green-800">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Transcript
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    No Transcript
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Right Sidebar */}
              <div className="space-y-4">
                {/* Add Student */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Add Student</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleAddStudent} className="space-y-3">
                      <div>
                        <Label>Student Email</Label>
                        <Input 
                          type="email" 
                          value={newStudentEmail}
                          onChange={(e) => setNewStudentEmail(e.target.value)}
                          placeholder="student@example.com"
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full">
                        <PlusCircle className="h-4 w-4 mr-2" />
                        Assign Student
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* Selected Student Actions */}
                {selectedStudent && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">
                        {selectedStudent.first_name} {selectedStudent.last_name}
                      </CardTitle>
                      <CardDescription>{selectedStudent.email}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Transcript Upload */}
                      <div>
                        <Label className="mb-2 block">Official Transcript</Label>
                        <input 
                          type="file" 
                          accept=".pdf"
                          onChange={(e) => handleTranscriptUpload(selectedStudent._id, e.target.files[0])}
                          className="hidden"
                          id={`transcript-${selectedStudent._id}`}
                        />
                        <label htmlFor={`transcript-${selectedStudent._id}`}>
                          <Button type="button" variant="outline" className="w-full" asChild>
                            <span>
                              <Upload className="h-4 w-4 mr-2" />
                              {selectedStudent.has_transcript ? 'Update Transcript' : 'Upload Transcript'}
                            </span>
                          </Button>
                        </label>
                      </div>

                      {/* Recommendation */}
                      <div>
                        <Label className="mb-2 block">Counselor Recommendation</Label>
                        <Textarea 
                          value={recommendation}
                          onChange={(e) => setRecommendation(e.target.value)}
                          placeholder="Write your recommendation for this student..."
                          rows={6}
                        />
                        <Button 
                          onClick={() => handleSubmitRecommendation(selectedStudent._id)}
                          disabled={!recommendation.trim()}
                          className="w-full mt-2"
                        >
                          Submit Recommendation
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Non-Partnered Cases Tab */}
          <TabsContent value="non-partnered">
            <div className="space-y-6">
              {/* Filters */}
              <Card>
                <CardContent className="pt-4">
                  <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                    <div className="relative flex-1 max-w-md">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input 
                        placeholder="Search by student, email, or university..." 
                        value={caseSearchQuery}
                        onChange={(e) => setCaseSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="case-search-input"
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      <Label className="text-sm text-gray-600">Filter by status:</Label>
                      <Select value={caseStatusFilter} onValueChange={setCaseStatusFilter}>
                        <SelectTrigger className="w-40" data-testid="case-status-filter">
                          <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Statuses</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="assigned">Assigned</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Cases Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold">{nonPartneredCases.filter(c => c.status === 'pending').length}</p>
                    <p className="text-sm opacity-90">Pending</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-500 to-cyan-500 text-white">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold">{nonPartneredCases.filter(c => c.status === 'assigned').length}</p>
                    <p className="text-sm opacity-90">Assigned</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold">{nonPartneredCases.filter(c => c.status === 'in_progress').length}</p>
                    <p className="text-sm opacity-90">In Progress</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-500 to-emerald-500 text-white">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold">{nonPartneredCases.filter(c => c.status === 'completed').length}</p>
                    <p className="text-sm opacity-90">Completed</p>
                  </CardContent>
                </Card>
              </div>

              {/* Cases List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="h-5 w-5" />
                    Non-Partnered University Applications
                  </CardTitle>
                  <CardDescription>
                    Students who selected universities not yet partnered with Wayo. Review profiles and provide one-on-one assistance.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredCases.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Briefcase className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="font-medium">No cases found</p>
                      <p className="text-sm">Cases will appear here when students select non-partnered universities</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredCases.map((caseItem) => (
                        <div 
                          key={caseItem._id}
                          className="p-4 border rounded-lg hover:border-purple-300 hover:bg-purple-50/50 transition-all cursor-pointer"
                          onClick={() => handleViewCaseDetail(caseItem)}
                          data-testid={`case-item-${caseItem._id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div className="bg-purple-100 p-2 rounded-full">
                                  <User className="h-5 w-5 text-purple-600" />
                                </div>
                                <div>
                                  <h3 className="font-semibold text-lg">{caseItem.student_name || 'Unknown Student'}</h3>
                                  <p className="text-sm text-gray-600 flex items-center gap-1">
                                    <Mail className="h-3 w-3" />
                                    {caseItem.student_email}
                                  </p>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-4 mt-3 text-sm">
                                <div className="flex items-center gap-1 text-gray-700">
                                  <Building2 className="h-4 w-4" />
                                  <span className="font-medium">{caseItem.college_name}</span>
                                </div>
                                <div className="flex items-center gap-1 text-green-700">
                                  <DollarSign className="h-4 w-4" />
                                  <span>Service Fee: ${caseItem.wayo_service_fee || 20}</span>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex flex-col items-end gap-2">
                              <Badge className={`${getStatusBadge(caseItem.status)} border`}>
                                {caseItem.status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                              </Badge>
                              <span className="text-xs text-gray-500">
                                {new Date(caseItem.created_at).toLocaleDateString()}
                              </span>
                              <ChevronRight className="h-5 w-5 text-gray-400 mt-2" />
                            </div>
                          </div>
                          
                          {caseItem.notes && caseItem.notes.length > 0 && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-xs text-gray-500 flex items-center gap-1">
                                <MessageSquare className="h-3 w-3" />
                                {caseItem.notes.length} note(s)
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Case Detail Modal */}
          <Dialog open={caseDetailOpen} onOpenChange={setCaseDetailOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              {selectedCase && (
                <>
                  <DialogHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <DialogTitle className="text-xl">Case Details</DialogTitle>
                        <DialogDescription>
                          Non-partnered university application assistance
                        </DialogDescription>
                      </div>
                      <Badge className={`${getStatusBadge(selectedCase.status)} border`}>
                        {selectedCase.status?.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                  </DialogHeader>
                  
                  <div className="space-y-6 mt-4">
                    {/* Student Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        <User className="h-5 w-5 text-purple-600" />
                        Student Information
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-500 text-sm">Name</Label>
                          <p className="font-medium">{selectedCase.student_name || 'Not provided'}</p>
                        </div>
                        <div>
                          <Label className="text-gray-500 text-sm">Email</Label>
                          <p className="font-medium">{selectedCase.student_email}</p>
                        </div>
                      </div>
                    </div>

                    {/* University Info */}
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-blue-600" />
                        Target University
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-500 text-sm">University</Label>
                          <p className="font-medium">{selectedCase.college_name}</p>
                        </div>
                        <div>
                          <Label className="text-gray-500 text-sm">Application Fee</Label>
                          <p className="font-medium">${selectedCase.college_app_fee || 'Varies'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Fee Summary */}
                    <div className="bg-green-50 rounded-lg p-4">
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        <DollarSign className="h-5 w-5 text-green-600" />
                        Fee Summary
                      </h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">University Application Fee</span>
                          <span>${selectedCase.college_app_fee || 'Varies'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Wayo Service Fee</span>
                          <span>${selectedCase.wayo_service_fee || 20}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t font-semibold">
                          <span>Total Estimated</span>
                          <span>${selectedCase.total_estimated_fee || 'TBD'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Student Profile Preview */}
                    {selectedCase.student_profile && (
                      <div className="bg-purple-50 rounded-lg p-4">
                        <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                          <BookOpen className="h-5 w-5 text-purple-600" />
                          Student Profile Summary
                        </h3>
                        <div className="space-y-3 text-sm">
                          {selectedCase.student_profile.personal_info && (
                            <div>
                              <Label className="text-gray-500">Contact</Label>
                              <p>{selectedCase.student_profile.personal_info.phone || 'No phone provided'}</p>
                            </div>
                          )}
                          {selectedCase.student_profile.education && Object.keys(selectedCase.student_profile.education).length > 0 && (
                            <div>
                              <Label className="text-gray-500">Education</Label>
                              <p>GPA: {selectedCase.student_profile.education.gpa || 'Not provided'}</p>
                            </div>
                          )}
                          {selectedCase.student_profile.activities && selectedCase.student_profile.activities.length > 0 && (
                            <div>
                              <Label className="text-gray-500">Activities</Label>
                              <p>{selectedCase.student_profile.activities.length} activities listed</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Status Update */}
                    <div className="bg-amber-50 rounded-lg p-4">
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        <Award className="h-5 w-5 text-amber-600" />
                        Update Status
                      </h3>
                      <div className="flex gap-2 flex-wrap">
                        {['pending', 'assigned', 'in_progress', 'completed'].map((status) => (
                          <Button
                            key={status}
                            variant={selectedCase.status === status ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => handleUpdateCaseStatus(selectedCase._id, status)}
                            data-testid={`status-btn-${status}`}
                          >
                            {status.replace('_', ' ').toUpperCase()}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Notes Section */}
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        <MessageSquare className="h-5 w-5 text-gray-600" />
                        Case Notes
                      </h3>
                      
                      {/* Existing Notes */}
                      {selectedCase.notes && selectedCase.notes.length > 0 ? (
                        <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
                          {selectedCase.notes.map((note, idx) => (
                            <div key={idx} className="bg-gray-50 p-3 rounded text-sm">
                              <p>{note.content}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(note.added_at).toLocaleString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm mb-4">No notes yet</p>
                      )}
                      
                      {/* Add Note Form */}
                      <div className="space-y-2">
                        <Textarea
                          placeholder="Add a note about this case..."
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          rows={3}
                          data-testid="case-note-input"
                        />
                        <Button 
                          onClick={() => handleAddNote(selectedCase._id)}
                          disabled={!newNote.trim()}
                          className="w-full"
                          data-testid="add-note-btn"
                        >
                          <PlusCircle className="h-4 w-4 mr-2" />
                          Add Note
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>

          {/* Deadlines Tab */}
          <TabsContent value="deadlines">
            <Card>
              <CardHeader>
                <CardTitle>Upcoming Application Deadlines</CardTitle>
                <CardDescription>Students with pending applications sorted by deadline</CardDescription>
              </CardHeader>
              <CardContent>
                {deadlines.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No upcoming deadlines</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {deadlines.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-semibold">{item.student_name}</p>
                          <p className="text-sm text-gray-600">{item.college_name}</p>
                        </div>
                        <div className="text-right">
                          <Badge variant={item.deadline < new Date().toISOString().split('T')[0] ? 'destructive' : 'outline'}>
                            <Clock className="h-3 w-3 mr-1" />
                            {item.deadline || 'No deadline'}
                          </Badge>
                          <div className="mt-2 flex items-center space-x-2">
                            <Progress value={item.progress} className="w-24 h-2" />
                            <span className="text-xs text-gray-500">{item.progress}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CounselorPortal;
